(() => {
  'use strict';
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const state = { library:null, config:null, view:'perspective', yaw:-0.72, pitch:-0.44, zoom:0.92, wireframe:true, dragging:false, x:0, y:0, frame:0, returnFocus:null, returnScrollY:0 };
  let projectionState = state;
  const inlineInstances = new Set();
  const libraryUrl = () => window.CKB_NEC3D_LIBRARY_URL || document.querySelector('[data-nec3d-library-url]')?.dataset.nec3dLibraryUrl;
  const views = {
    perspective:{ yaw:-0.72, pitch:-0.44, label:'3D perspective · +Z is up' },
    front:{ yaw:0, pitch:-Math.PI/2, label:'Front · X–Z plane · +Z is up' },
    side:{ yaw:Math.PI/2, pitch:-Math.PI/2, label:'Side · Y–Z plane · +Z is up' },
    top:{ yaw:0, pitch:0, label:'Top · X–Y plane · looking toward −Z' },
  };

  // Search operators use in the field: punctuation, spaces and case must never
  // be the difference between finding F-LOOP, TACYAGI-70 or a model identity.
  const searchKey = value => String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

  const decode = value => {
    const raw = atob(value), output = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
    return output;
  };

  const colour = relative => {
    const t = Math.max(0, Math.min(1, (relative + 30) / 30));
    const stops = [[0,[89,36,190]],[.25,[39,91,213]],[.48,[21,174,189]],[.68,[82,196,105]],[.84,[239,201,63]],[1,[246,94,36]]];
    for (let i = 0; i < stops.length - 1; i += 1) {
      const [a,c1] = stops[i], [b,c2] = stops[i + 1];
      if (t <= b) {
        const u = (t - a) / (b - a);
        return `rgb(${c1.map((v,j) => Math.round(v + (c2[j] - v) * u)).join(',')})`;
      }
    }
    return 'rgb(246,94,36)';
  };

  // NEC rows are emitted by azimuth (phi) first and elevation angle (theta)
  // second. Keeping this addressing explicit prevents a valid grid from being
  // displayed as an inverted or rotationally scrambled surface.
  const valueAt = (dataset, thetaIndex, phiIndex) => {
    if (dataset.valueOrder === 'theta-major-phi-minor') return dataset.values[thetaIndex * dataset.phiCount + phiIndex];
    return dataset.values[phiIndex * dataset.thetaCount + thetaIndex];
  };

  const projectCartesian = (x, y, z, width, height, db = 0) => {
    const cy = Math.cos(projectionState.yaw), sy = Math.sin(projectionState.yaw);
    [x,y] = [x * cy - y * sy, x * sy + y * cy];
    const cx = Math.cos(projectionState.pitch), sx = Math.sin(projectionState.pitch);
    [y,z] = [y * cx - z * sx, y * sx + z * cx];
    const scale = Math.min(width, height) * .4 * projectionState.zoom;
    return { x:width / 2 + x * scale, y:height * .53 - y * scale, z, db };
  };

  const project = (theta, phi, db, width, height) => {
    const radius = Math.max(.035, 10 ** (Math.max(-30, db) / 20));
    const st = Math.sin(theta), ct = Math.cos(theta), sp = Math.sin(phi), cp = Math.cos(phi);
    return projectCartesian(radius * st * cp, radius * st * sp, radius * ct, width, height, db);
  };

  const drawAxes = (ctx, width, height) => {
    const ring = [];
    for (let index = 0; index <= 96; index += 1) {
      const angle = index / 96 * Math.PI * 2;
      ring.push(projectCartesian(Math.cos(angle), Math.sin(angle), 0, width, height));
    }
    ctx.save();
    ctx.strokeStyle = 'rgba(54,45,37,.24)';
    ctx.setLineDash([5,6]);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ring.forEach((point,index) => index ? ctx.lineTo(point.x,point.y) : ctx.moveTo(point.x,point.y));
    ctx.stroke();
    ctx.setLineDash([]);
    const centre = projectCartesian(0,0,0,width,height);
    const axes = [
      // Keep every arrow and label inside the smallest supported mobile canvas.
      // The dashed unit circle remains the outer model reference.
      { name:'X', colour:'#d92532', negative:[-.94,0,0], positive:[.94,0,0] },
      { name:'Y', colour:'#16865a', negative:[0,-.94,0], positive:[0,.94,0] },
      { name:'Z', colour:'#2667c9', negative:[0,0,-.94], positive:[0,0,.94] },
    ];
    const label = (value, point, colourValue) => {
      ctx.save(); ctx.textAlign='center'; ctx.textBaseline='middle'; ctx.font='900 18px system-ui,sans-serif';
      ctx.lineWidth=7; ctx.strokeStyle='rgba(255,255,255,.98)'; ctx.strokeText(value,point.x,point.y);
      ctx.fillStyle=colourValue; ctx.fillText(value,point.x,point.y); ctx.restore();
    };
    const arrow = (from,to,colourValue) => {
      const angle=Math.atan2(to.y-from.y,to.x-from.x), size=11;
      ctx.save(); ctx.fillStyle=colourValue; ctx.beginPath(); ctx.moveTo(to.x,to.y);
      ctx.lineTo(to.x-size*Math.cos(angle-.48),to.y-size*Math.sin(angle-.48));
      ctx.lineTo(to.x-size*Math.cos(angle+.48),to.y-size*Math.sin(angle+.48)); ctx.closePath(); ctx.fill(); ctx.restore();
    };
    axes.forEach(axis => {
      const negative=projectCartesian(...axis.negative,width,height), positive=projectCartesian(...axis.positive,width,height);
      const span=Math.hypot(positive.x-negative.x,positive.y-negative.y);
      if (span < 28) {
        ctx.save(); ctx.strokeStyle=axis.colour; ctx.lineWidth=3; ctx.beginPath(); ctx.arc(centre.x,centre.y,8,0,Math.PI*2); ctx.stroke(); ctx.restore();
        label(`+${axis.name}${axis.name==='Z'?' · UP':''}`,{x:centre.x+78,y:centre.y-21},axis.colour);
        label(`−${axis.name}`,{x:centre.x+78,y:centre.y+14},axis.colour);
        return;
      }
      ctx.save(); ctx.strokeStyle=axis.colour; ctx.lineWidth=2.6; ctx.lineCap='round';
      ctx.setLineDash([8,7]); ctx.beginPath(); ctx.moveTo(negative.x,negative.y); ctx.lineTo(centre.x,centre.y); ctx.stroke();
      ctx.setLineDash([]); ctx.beginPath(); ctx.moveTo(centre.x,centre.y); ctx.lineTo(positive.x,positive.y); ctx.stroke(); ctx.restore();
      arrow(centre,positive,axis.colour);
      const unit=(point,origin,amount=18) => { const dx=point.x-origin.x,dy=point.y-origin.y,d=Math.max(1,Math.hypot(dx,dy)); return {x:point.x+dx/d*amount,y:point.y+dy/d*amount}; };
      label(`+${axis.name}${axis.name==='Z'?' · UP':''}`,unit(positive,centre),axis.colour);
      label(`−${axis.name}`,unit(negative,centre),axis.colour);
    });
    ctx.save(); ctx.beginPath(); ctx.arc(centre.x,centre.y,5,0,Math.PI*2); ctx.fillStyle='#362d25'; ctx.fill(); ctx.restore();
    // The origin point is sufficient; text here obscures the radiation volume.
    ctx.restore();
  };

  const installationFor = config => {
    const key = searchKey(`${config?.label || ''} ${config?.modelId || ''} ${config?.package || ''}`);
    if (/chavehicle/.test(key)) {
      const fsr=/fsrx/.test(key);
      return { title:'Stationary vehicle with deployed RF return', detail:`The radiator follows +Z. +X is vehicle-forward and +Y is driver-side/left. ${fsr ? 'The CHA FSR X return occupies the four diagonal quadrants between ±X and ±Y.' : 'The two 25 ft wire radials occupy +Y and −Y.'} The representative vehicle body is nearby conductive structure, not a substitute return.` };
    }
    if (/mcc.*caphat/.test(key)) return { title:'MCC + CAP HAT vertical', detail:'The governed radiator rises on +Z. The exact case record names either two radials on +X and −X or four radials on +X, +Y, −X and −Y. The tuned MCC value is a model-derived electrical state—not a physical customer setting.' };
    if (/lzsloper|lazyl/.test(key)) return { title:'MPAS Lite Inverted Lazy L', detail:'The fully extended SS17 or SS25 rises on +Z, the 60 ft LZ wire slopes toward +X, and the four 25 ft radials follow +X, +Y, −X and −Y on the ground plane.' };
    if (/milext.*lzsloper/.test(key)) return { title:'MIL EXT + whip + LZ SLOPER', detail:'The MIL EXT and whip rise on +Z, the 60 ft LZ wire slopes along +X, and the two 25 ft radials occupy +Y and −Y.' };
    if (/floop|magneticloop/.test(key)) return { title:'Vertical magnetic loop', detail:'The governed main-loop plane is X–Z. +Z is vertical; Y is perpendicular to the loop plane and therefore the broadside/null reference.' };
    if (/skyloop/.test(key)) return { title:'Horizontal near-square loop', detail:'The governed loop sides follow X and Y. +Z is installation height above the XY ground plane.' };
    if (/invertedl/.test(key)) return { title:'Inverted-L wire', detail:'The governed vertical section follows +Z and the horizontal top wire follows +X. Y is perpendicular to the horizontal wire reference.' };
    if (/invertedv|vdipole/.test(key)) return { title:'Inverted-V wire', detail:'The governed wire legs follow ±X from an apex on +Z. Y is the perpendicular broadside reference.' };
    if (/sloper|sloping/.test(key)) return { title:'Sloping wire', detail:'The governed wire rises from −X toward its high support at +X; +Z is height. Y is the perpendicular broadside reference.' };
    if (/horizontal|nvis/.test(key)) return { title:'Horizontal wire', detail:'The governed radiator follows X at its recorded +Z height. Y is the perpendicular broadside reference; the XY plane is ground.' };
    if (/tacyagi|yagi/.test(key)) return { title:'Directional Yagi', detail:'The governed boom follows X; Y is the element/cross-boom reference and +Z is vertical. Consult the case record for the declared forward direction.' };
    if (/vertical|prv|bv|ss17|ss25|milwhip|mpas/.test(key)) return { title:'Vertical radiator', detail:'The governed radiator rises on +Z. X and Y define the horizontal ground plane; compass bearing is immaterial without a directional attachment.' };
    return { title:'Controlled model reference', detail:'X follows the primary governed geometry reference, Y is its perpendicular horizontal reference and +Z is vertical. Unsupported dimensions or compass bearings are not inferred.' };
  };

  const drawRelativeLevelGrid = (ctx, width, height) => {
    const levels = [0, -3, -6, -10, -20, -30];
    const radiusFor = db => Math.max(.035, 10 ** (db / 20));
    ctx.save();
    ctx.lineWidth = 1;
    for (let spoke = 0; spoke < 12; spoke += 1) {
      const angle = spoke / 12 * Math.PI * 2;
      const centre = projectCartesian(0,0,0,width,height);
      const outer = projectCartesian(Math.cos(angle),Math.sin(angle),0,width,height);
      ctx.beginPath();
      ctx.moveTo(centre.x,centre.y);
      ctx.lineTo(outer.x,outer.y);
      ctx.strokeStyle = spoke % 3 === 0 ? 'rgba(54,45,37,.30)' : 'rgba(54,45,37,.16)';
      ctx.stroke();
    }
    levels.forEach((db,levelIndex) => {
      const radius = radiusFor(db);
      ctx.beginPath();
      for (let index = 0; index <= 120; index += 1) {
        const angle = index / 120 * Math.PI * 2;
        const point = projectCartesian(radius*Math.cos(angle),radius*Math.sin(angle),0,width,height);
        if (index) ctx.lineTo(point.x,point.y); else ctx.moveTo(point.x,point.y);
      }
      ctx.closePath();
      ctx.strokeStyle = levelIndex === 0 ? 'rgba(54,45,37,.42)' : 'rgba(54,45,37,.27)';
      ctx.setLineDash(levelIndex === 0 ? [] : [4,4]);
      ctx.stroke();
    });
    ctx.setLineDash([]);
    ctx.restore();
  };

  const drawRelativeLevelLabels = (ctx, width, height) => {
    const levels = [0, -3, -6, -10, -20, -30];
    const radiusFor = db => Math.max(.035, 10 ** (db / 20));
    const offsets = [{x:8,y:-9},{x:8,y:-7},{x:8,y:-6},{x:8,y:-5},{x:14,y:-16},{x:17,y:10}];
    ctx.save();
    ctx.textAlign='left'; ctx.textBaseline='middle'; ctx.font='900 11px system-ui,sans-serif';
    levels.forEach((db,index) => {
      const point=projectCartesian(0,radiusFor(db),0,width,height);
      const text=(db === 0 ? '0' : '−' + Math.abs(db)) + ' dB';
      const labelX=point.x+offsets[index].x, labelY=point.y+offsets[index].y;
      ctx.beginPath(); ctx.moveTo(point.x,point.y); ctx.lineTo(labelX-2,labelY);
      ctx.strokeStyle='rgba(54,45,37,.42)'; ctx.lineWidth=1; ctx.stroke();
      ctx.fillStyle='rgba(54,45,37,.58)'; ctx.fillText(text,labelX,labelY);
    });
    ctx.restore();
  };

  const renderSurface = (canvas, config, renderState, options = {}) => {
      if (!canvas || !state.library || !config) return;
      const dataset = state.library.datasets[config.datasetId];
      if (!dataset) return;
      if (!dataset.values) dataset.values = decode(dataset.valuesBase64);
      const previousProjectionState = projectionState;
      projectionState = renderState;
      try {
      const rect = canvas.getBoundingClientRect(), ratio = Math.min(options.maxRatio || 2, window.devicePixelRatio || 1);
      canvas.width = Math.max(options.minWidth || 600, Math.round(rect.width * ratio));
      canvas.height = Math.max(options.minHeight || 500, Math.round(rect.height * ratio));
      const ctx = canvas.getContext('2d');
      ctx.scale(ratio, ratio);
      const width = canvas.width / ratio, height = canvas.height / ratio;
      ctx.clearRect(0, 0, width, height);
      const gradient = ctx.createLinearGradient(0, 0, 0, height);
      gradient.addColorStop(0, '#ffffff'); gradient.addColorStop(1, '#f3efe5');
      ctx.fillStyle = gradient; ctx.fillRect(0, 0, width, height);

      const tc = dataset.thetaCount, pc = dataset.phiCount;
      const thetaStep = dataset.thetaStepDegrees * Math.PI / 180, phiStep = dataset.phiStepDegrees * Math.PI / 180;
      const thetaStart = (dataset.thetaStartDegrees || 0) * Math.PI / 180, phiStart = (dataset.phiStartDegrees || 0) * Math.PI / 180;
      const thetaStride = Math.max(1, Math.ceil(tc / 92));
      const points = [];
      for (let ti = 0; ti < tc; ti += thetaStride) {
        const row = [];
        for (let pi = 0; pi < pc; pi += 1) {
          const q = valueAt(dataset, ti, pi), db = -30 + q / 255 * 30;
          row.push(project(thetaStart + ti * thetaStep, phiStart + pi * phiStep, db, width, height));
        }
        points.push(row);
      }
      const cells = [];
      for (let ti = 0; ti < points.length - 1; ti += 1) for (let pi = 0; pi < pc; pi += 1) {
        const pn = (pi + 1) % pc, polygon = [points[ti][pi],points[ti + 1][pi],points[ti + 1][pn],points[ti][pn]];
        cells.push({ polygon, z:polygon.reduce((sum,p) => sum + p.z, 0) / 4, db:polygon.reduce((sum,p) => sum + p.db, 0) / 4 });
      }
      if (renderState.wireframe) drawRelativeLevelGrid(ctx,width,height);
      cells.sort((a,b) => a.z - b.z).forEach(cell => {
        ctx.beginPath(); cell.polygon.forEach((point,index) => index ? ctx.lineTo(point.x,point.y) : ctx.moveTo(point.x,point.y)); ctx.closePath();
        ctx.fillStyle = colour(cell.db); ctx.globalAlpha = .78; ctx.fill();
        ctx.globalAlpha = .22; ctx.strokeStyle = '#4f463b'; ctx.lineWidth = .55; ctx.stroke();
      });
      ctx.globalAlpha = 1;
      drawAxes(ctx,width,height);
      if (renderState.wireframe) drawRelativeLevelLabels(ctx,width,height);
      ctx.fillStyle = '#362d25'; ctx.font = '800 12px system-ui,sans-serif'; ctx.fillText('NORMALIZED SHAPE', 18, 26);
      ctx.font = '12px system-ui,sans-serif'; ctx.fillStyle = '#61594f'; ctx.fillText(views[renderState.view]?.label || 'Custom view - height is up', 18, 46);
      ctx.fillText('Drag to rotate - pinch or scroll to zoom', 18, 64);
      } finally {
        projectionState = previousProjectionState;
      }
  };

  const render = () => {
    cancelAnimationFrame(state.frame);
    state.frame = requestAnimationFrame(() => {
      renderSurface($('[data-nec3d-canvas]'), state.config, state);
    });
  };

  const renderInline = instance => {
    cancelAnimationFrame(instance.frame);
    instance.frame = requestAnimationFrame(() => {
      if (!instance.canvas.isConnected) { inlineInstances.delete(instance); return; }
      renderSurface(instance.canvas, instance.config, instance, { minWidth:420, minHeight:280, maxRatio:1.5 });
    });
  };

  const mountInline = async (canvas, modelId) => {
    if (!canvas || !modelId) throw new Error('An exact canvas and model identity are required.');
    inlineInstances.forEach(item => { if (!item.canvas.isConnected) inlineInstances.delete(item); });
    const library = await load();
    const config = library.configs.find(item => item.modelId === modelId);
    if (!config) throw new Error(`No governed 3D dataset matches ${modelId}.`);
    let instance = Array.from(inlineInstances).find(item => item.canvas === canvas);
    if (!instance) {
      instance = { canvas, config, view:'perspective', yaw:views.perspective.yaw, pitch:views.perspective.pitch, zoom:.88, wireframe:true, dragging:false, x:0, y:0, frame:0 };
      inlineInstances.add(instance);
      const endPointer = () => { instance.dragging=false; };
      canvas.addEventListener('pointerdown', event => { event.preventDefault(); instance.dragging=true; instance.x=event.clientX; instance.y=event.clientY; try{canvas.setPointerCapture(event.pointerId);}catch(error){/* Pointer capture is an enhancement, not a rendering requirement. */} });
      canvas.addEventListener('pointermove', event => { if(!instance.dragging)return;event.preventDefault();instance.view='custom';instance.yaw+=(event.clientX-instance.x)*.009;instance.pitch=Math.max(-1.45,Math.min(1.45,instance.pitch+(event.clientY-instance.y)*.009));instance.x=event.clientX;instance.y=event.clientY;renderInline(instance); });
      canvas.addEventListener('pointerup',endPointer);canvas.addEventListener('pointercancel',endPointer);canvas.addEventListener('lostpointercapture',endPointer);
      canvas.addEventListener('wheel',event=>{event.preventDefault();instance.zoom=Math.max(.45,Math.min(1.7,instance.zoom*(event.deltaY>0?.92:1.08)));renderInline(instance);},{passive:false});
      let inlinePinch=0;canvas.addEventListener('touchmove',event=>{if(event.touches.length!==2)return;event.preventDefault();const distance=Math.hypot(event.touches[0].clientX-event.touches[1].clientX,event.touches[0].clientY-event.touches[1].clientY);if(inlinePinch)instance.zoom=Math.max(.45,Math.min(1.7,instance.zoom*distance/inlinePinch));inlinePinch=distance;renderInline(instance);},{passive:false});canvas.addEventListener('touchend',()=>{inlinePinch=0;});
      canvas.addEventListener('keydown',event=>{const handled=['ArrowLeft','ArrowRight','ArrowUp','ArrowDown','+','=','-'].includes(event.key);if(!handled)return;event.preventDefault();if(event.key==='ArrowLeft')instance.yaw-=.12;if(event.key==='ArrowRight')instance.yaw+=.12;if(event.key==='ArrowUp')instance.pitch=Math.max(-1.45,instance.pitch-.12);if(event.key==='ArrowDown')instance.pitch=Math.min(1.45,instance.pitch+.12);if(event.key==='+'||event.key==='=')instance.zoom=Math.min(1.7,instance.zoom*1.08);if(event.key==='-')instance.zoom=Math.max(.45,instance.zoom*.92);instance.view='custom';renderInline(instance);});
    }
    instance.config = config;
    renderInline(instance);
    return { label:config.label, modelId:config.modelId, frequencyMHz:config.frequencyMHz };
  };

  const selectConfig = config => {
    if (!config || !state.library) return;
    const lab = $('[data-nec3d-laboratory]');
    if (!lab) return;
    state.config = config;
    const dataset = state.library.datasets[config.datasetId];
    $('[data-nec3d-title]', lab).textContent = config.label;
    const modelReference=$('[data-nec3d-model]', lab);
    modelReference.textContent='Modeled NEC-4.2 configuration';
    modelReference.dataset.sourceModelId=config.modelId;
    $('[data-nec3d-frequency]', lab).textContent = `${Number(config.frequencyMHz).toFixed(config.frequencyMHz < 100 ? 3 : 1)} MHz`;
    $('[data-nec3d-gain]', lab).textContent = Number.isFinite(Number(dataset.sourcePeakNecGainDbi)) ? `${Number(dataset.sourcePeakNecGainDbi).toFixed(2)} dBi modeled peak` : 'Not published for this normalized pattern';
    $('[data-nec3d-grid]', lab).textContent = `${dataset.thetaCount} x ${dataset.phiCount} native angular samples`;
    $('[data-nec3d-scope]', lab).textContent = config.scope;
    const installation = installationFor(config);
    $('[data-nec3d-setup]', lab).textContent = installation.title;
    $('[data-nec3d-orientation]', lab).textContent = installation.detail;
    const select = $('[data-nec3d-select]', lab); if (select) select.value = config.id;
    window.CHA_NEC2D_POLAR?.showConfig($('[data-nec2d-polar-live]', lab), state.library, config);
    render();
  };

  const rebuildOptions = (library, query = '') => {
    const select = $('[data-nec3d-select]'); if (!select) return;
    const needle = searchKey(query);
    const matches = library.configs.filter(config => !needle || searchKey(`${config.label} ${config.modelId} ${config.package}`).includes(needle));
    select.innerHTML = '';
    let current = '';
    matches.forEach(config => {
      if (config.package !== current) {
        current = config.package;
        const group = document.createElement('optgroup'); group.label = current.replaceAll('_',' '); group.dataset.package = current; select.append(group);
      }
      const option = document.createElement('option'); option.value = config.id; option.textContent = config.label; select.lastElementChild.append(option);
    });
    if (!matches.length) {
      const option = document.createElement('option'); option.textContent = 'No approved configuration matches this search'; option.disabled = true; select.append(option);
    }
    $('[data-nec3d-search-status]').textContent = `${matches.length} of ${library.configurationCount} approved configurations shown.`;
    if (state.config && matches.some(config => config.id === state.config.id)) select.value = state.config.id;
    return matches;
  };

  const populate = library => {
    const select = $('[data-nec3d-select]'); if (!select) return;
    rebuildOptions(library);
    select.addEventListener('change', () => selectConfig(library.configs.find(config => config.id === select.value)));
    const search = $('[data-nec3d-search]');
    search.addEventListener('input', () => {
      const matches = rebuildOptions(library, search.value);
      if (matches.length === 1) selectConfig(matches[0]);
    });
    $('[data-nec3d-count]').textContent = `${library.configurationCount} approved configurations - ${library.uniqueDatasetCount} unique datasets`;
  };

  const load = async () => {
    if (state.library) return state.library;
    const status = $('[data-nec3d-status]'); status.textContent = 'Loading the governed NEC-4.2 library…';
    try {
      const response = await fetch(libraryUrl(), { credentials:'same-origin' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      state.library = await response.json(); populate(state.library); status.textContent = 'Reviewed library ready';
      return state.library;
    } catch (error) {
      status.textContent = '3D source file unavailable';
      $('[data-nec3d-error]').hidden = false;
      throw error;
    }
  };

  const open = async (modelId, trigger = null) => {
    const lab = $('[data-nec3d-laboratory]'); if (!lab) return;
    const focusedPattern = Boolean(modelId);
    state.returnFocus = trigger instanceof HTMLElement ? trigger : document.activeElement;
    state.returnScrollY = window.scrollY;
    if (lab.parentElement !== document.body) document.body.append(lab);
    lab.dataset.nec3dMode = focusedPattern ? 'focus' : 'laboratory';
    $('[data-nec3d-close]', lab).textContent = focusedPattern ? 'Close and return' : 'Close 3D laboratory';
    $('#CKBNEC3DTitle', lab).textContent = focusedPattern ? 'Interactive 3D pattern' : 'Explore the complete radiation shape';
    lab.hidden = false; document.documentElement.classList.add('ckb-nec3d-open');
    const library = await load().catch(() => null); if (!library) return;
    const config = (modelId && library.configs.find(item => item.modelId === modelId)) || library.configs[0];
    selectConfig(config);
    requestAnimationFrame(() => {
      lab.scrollTop = 0;
      render();
      $('[data-nec3d-close]', lab)?.focus({preventScroll:true});
    });
  };

  const close = () => {
    const lab = $('[data-nec3d-laboratory]');
    if (lab) { lab.hidden = true; delete lab.dataset.nec3dMode; }
    document.documentElement.classList.remove('ckb-nec3d-open');
    window.scrollTo({top:state.returnScrollY,left:0,behavior:'auto'});
    state.returnFocus?.focus?.({preventScroll:true});
    state.returnFocus = null;
  };
  const preset = name => {
    const selected = views[name] || views.perspective;
    state.view = views[name] ? name : 'perspective';
    state.yaw = selected.yaw;
    state.pitch = selected.pitch;
    $$('[data-nec3d-view]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.nec3dView === state.view)));
    render();
  };

  let initialized = false;
  let initializationObserver = null;
  const initialize = () => {
    if (initialized) return;
    const canvas = $('[data-nec3d-canvas]');
    if (!canvas) {
      if (!initializationObserver) {
        initializationObserver = new MutationObserver(() => {
          if (!$('[data-nec3d-canvas]')) return;
          initializationObserver.disconnect();
          initializationObserver = null;
          initialize();
        });
        initializationObserver.observe(document.documentElement, { childList:true, subtree:true });
      }
      return;
    }
    initialized = true;
    document.addEventListener('click', event => {
      const trigger = event.target.closest('[data-nec3d-open]'); if (trigger) { event.preventDefault(); open(trigger.dataset.nec3dModel || '', trigger); }
      if (event.target.closest('[data-nec3d-close]')) close();
      const view = event.target.closest('[data-nec3d-view]'); if (view) preset(view.dataset.nec3dView);
      if (event.target.closest('[data-nec3d-wireframe]')) { state.wireframe = !state.wireframe; $('[data-nec3d-wireframe]').setAttribute('aria-pressed',String(state.wireframe)); render(); }
      if (event.target.closest('[data-nec3d-reset]')) { state.zoom=.92; preset('perspective'); }
    });
    document.addEventListener('keydown', event => {
      const lab = $('[data-nec3d-laboratory]');
      if (!lab || lab.hidden || !document.documentElement.classList.contains('ckb-nec3d-open')) return;
      if (event.key === 'Escape') { event.preventDefault(); close(); return; }
      if (event.key !== 'Tab') return;
      const focusable = $$('button:not([disabled]),input:not([disabled]),select:not([disabled]),canvas[tabindex]', lab).filter(element => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0]; const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
    const endPointer = () => { state.dragging=false; };
    canvas.addEventListener('pointerdown', event => { event.preventDefault(); state.dragging=true; state.x=event.clientX; state.y=event.clientY; try{canvas.setPointerCapture(event.pointerId);}catch(error){/* Brave can decline capture while an embedded frame settles. */} });
    canvas.addEventListener('pointermove', event => { if(!state.dragging)return; event.preventDefault(); state.view='custom'; $$('[data-nec3d-view]').forEach(button => button.setAttribute('aria-pressed','false')); state.yaw+=(event.clientX-state.x)*.009; state.pitch=Math.max(-1.45,Math.min(1.45,state.pitch+(event.clientY-state.y)*.009)); state.x=event.clientX;state.y=event.clientY;render(); });
    canvas.addEventListener('pointerup', endPointer); canvas.addEventListener('pointercancel', endPointer); canvas.addEventListener('lostpointercapture', endPointer); window.addEventListener('pointerup',endPointer,{passive:true});
    canvas.addEventListener('wheel', event => { event.preventDefault(); state.zoom=Math.max(.45,Math.min(1.7,state.zoom*(event.deltaY>0?.92:1.08)));render(); }, {passive:false});
    let pinch=0; canvas.addEventListener('touchmove', event => { if(event.touches.length!==2)return; event.preventDefault(); const distance=Math.hypot(event.touches[0].clientX-event.touches[1].clientX,event.touches[0].clientY-event.touches[1].clientY); if(pinch)state.zoom=Math.max(.45,Math.min(1.7,state.zoom*distance/pinch));pinch=distance;render(); },{passive:false}); canvas.addEventListener('touchend',()=>{pinch=0});
    canvas.addEventListener('keydown',event=>{const handled=['ArrowLeft','ArrowRight','ArrowUp','ArrowDown','+','=','-'].includes(event.key);if(!handled)return;event.preventDefault();if(event.key==='ArrowLeft')state.yaw-=.12;if(event.key==='ArrowRight')state.yaw+=.12;if(event.key==='ArrowUp')state.pitch=Math.max(-1.45,state.pitch-.12);if(event.key==='ArrowDown')state.pitch=Math.min(1.45,state.pitch+.12);if(event.key==='+'||event.key==='=')state.zoom=Math.min(1.7,state.zoom*1.08);if(event.key==='-')state.zoom=Math.max(.45,state.zoom*.92);state.view='custom';render();});
    window.addEventListener('resize', () => { render(); inlineInstances.forEach(renderInline); }, {passive:true});
    const params = new URLSearchParams(location.search); if (params.get('tool') === 'patterns3d') setTimeout(() => open(params.get('model') || ''),100);
    preset('perspective');
  };
  window.CKB_NEC3D = { open, close, mountInline, version:'1.5.0' };
  document.dispatchEvent(new CustomEvent('ckb:nec3d-ready'));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',initialize); else initialize();
})();
